import { defineCopy } from '@floorfile/i18n'

/**
 * Deviations & OOS investigations (localization plan Phase 5, chunk 5b-4).
 *
 * Two related records in one slice: a **deviation** is a documented departure
 * from an approved procedure or spec (planned, with approval before its
 * effectivity window, or unplanned and reviewed after the fact), and an **OOS
 * investigation** is the phased response to an out-of-specification result
 * (Phase 1 rules out lab/measurement error, Phase 2 is the full manufacturing
 * investigation, and the conclusion is e-signed).
 *
 * ## Six enums were reaching the screen raw, on five surfaces
 *
 * `DeviationKind`, `DeviationStatus`, `OosStatus`, `OosPhase1Outcome`,
 * `OosConclusion` and `OosSourceType` were all rendered as their contract
 * tokens: `<td>{d.kind}</td>` printed `PLANNED`, `<StatusBadge
 * label={d.status}>` printed `UNDER_REVIEW`, `<td>{o.phase1Outcome}</td>`
 * printed `ESCALATE`, and four `<select>`s listed the bare codes as `<option>`
 * children. **The lint rule sees none of it** — a property access is not a
 * literal, and an `<option>` whose child is an identifier is not either — which
 * is why a register page with 35 flagged literals also had three unlabelled
 * enums on it.
 *
 * **English changes** on all six. Checked against `apps/e2e` first:
 * `deviations-oos.spec.ts` drives this module entirely through the API and its
 * only UI assertion is the account menu; `esign.spec.ts` and
 * `a11y/routes.spec.ts` seed deviations but assert no deviation words. No text
 * locator anywhere in the suite reads one of these tokens.
 *
 * ## Two independent builders for `DeviationStatus`, and they had drifted
 *
 * The register's status filter hand-wrote a seven-element array of status
 * codes; the contract's `DeviationStatus` declares the same seven in a
 * different order (`REJECTED` and `IN_EFFECT` are transposed), so the filter
 * list and the badge were already two sources for one enum. All four selects
 * in this slice are now built from the contract enum's `.options` with the
 * words from this catalogue, so they cannot disagree again — the same fix
 * `ncr` made for its disposition and status selects.
 *
 * ## The Phase-1 outcome is TWO groups, not one lowercase call
 *
 * The badge reads "Measurement error — invalidate" while the radio beside the
 * textarea reads "Measurement error" and the register column reads the raw
 * token. Those are three lengths of the same fact, so `phase1Outcome` (short,
 * for the radio and the column) and `phase1Verdict` (the badge sentence) are
 * separate groups rather than one string a caller truncates. Same shape for
 * `oosSource` (the list-page caption) and `oosSourceDescription` (the detail
 * header's whole sentence).
 *
 * ## What is NOT fixed here: the raw ISO dates
 *
 * Five sites render a date by slicing the ISO string —
 * `effectiveFrom.slice(0,10)`, `.slice(0,16).replace('T',' ')` on the window,
 * the approval stamp and `concludedAt`. That is the deferred ISO-date sweep the
 * plan records as debt, and it changes English output, so it stays out of a
 * string-migration diff. What this chunk DOES do is give the sweep a seam: the
 * surrounding words are now messages with `{from}` / `{to}` / `{date}`
 * placeholders (`window`, `approvalStamp`, `oosDetail.concluded`), so when the
 * sweep lands it changes what is PASSED and not the sentence around it. The
 * five sites are recorded in the plan's debt list.
 *
 * ## `open` was a word inside an expression
 *
 * `` `${from} → ${to ?? 'open'}` `` built the effectivity window by template
 * literal, with an English word for the open-ended case buried in a `??`. It is
 * `window` + `windowOpenEnded` now — the arrow's position and the word are both
 * the locale's to decide.
 */
export const deviationsOosCopy = defineCopy('deviations-oos', {
  offlineAction: { recordDeviation: 'Record deviation', openOos: 'Open an out-of-spec investigation' },
  /** The same two as the create dialog's radio cards, which say what each
   * means rather than naming it. */
  kindChoice: {
    PLANNED: 'Planned (approve before)',
    UNPLANNED: 'Unplanned (found after)',
  },
  /** And as the detail header's one-line summary of the record. */
  kindSummary: {
    PLANNED: 'Planned departure, approved before its window',
    UNPLANNED: 'Unplanned departure, found after the fact',
  },

  /** `OosConclusion` — the signed verdict. Was raw on the badge and in the
   * filter `<select>`. */
  conclusion: {
    /** QMS-005. The French has read `Résultat hors spécifications confirmé`
     * since it was written; this is English catching up. */
    CONFIRMED_OOS: 'Confirmed out-of-spec result',
    INVALIDATED: 'Invalidated',
  },

  /** `OosPhase1Outcome`, short — the radio card and the register column. */
  phase1Outcome: {
    INVALIDATE: 'Measurement error',
    ESCALATE: 'Escalate to Phase 2',
  },
  /** The same outcome as the recorded verdict badge — a whole clause, because
   * it states the finding AND what follows from it. */
  phase1Verdict: {
    INVALIDATE: 'Measurement error — invalidate',
    ESCALATE: 'Real result — escalate to Phase 2',
  },

  /** `OosSourceType`, as the caption under a register row. */
  oosSource: {
    manual: 'Manual',
    check: 'From a check',
    incoming_inspection: 'From incoming inspection',
  },
  /** The same three as the detail page's header sentence. */
  oosSourceDescription: {
    manual: 'Manually recorded out-of-spec result',
    check: 'Opened from a check result',
    incoming_inspection: 'Opened from an incoming inspection',
  },

  /**
   * The effectivity window, rendered on the register row and the detail page.
   * One message: the arrow's position between the two dates is not ours to fix.
   */
  window: '{from} → {to}',
  /** A window with no closing date. Was `?? 'open'` inside a template literal. */
  windowOpenEnded: 'open',

  deviations: {
    title: 'Deviations',
    description:
      'Document departures from approved procedures — planned with prior approval, or unplanned with review.',
    exportLabel: 'Export',
    kindFilter: 'Filter by kind',
    allKinds: 'All kinds',
    statusFilter: 'Filter by status',
    allStatuses: 'All statuses',
    from: 'From',
    to: 'To',
    search: 'Search deviations',
    toOos: 'Out-of-spec investigations',
    loading: 'Loading…',
    loadFailed: 'Deviations could not be loaded.',
    region: 'Deviations',
    /**
     * Lowercase: read inside "Sorted by {field}, ascending".
     *
     * **One entry per field in `DeviationSort`, not per visible header.**
     * `createdAt` has no column of its own — it is the DEFAULT sort
     * (`-createdAt`), so it is the field announced on first load and there is
     * no header to notice it by. `sort-announcements.test.ts` reads the
     * contract's sort spec for exactly this reason.
     *
     * **Named `deviationSortField`, not `sortField`, because this slice has
     * TWO registers.** The guard finds a group by KEY anywhere in the
     * catalogue and fails on more than one match rather than guessing — it
     * sees `sortAnnouncement(sort, c.sortField)` through a local alias and
     * cannot tell `deviations` from `oos`. Every other migrated slice owns one
     * register and can use the plain name; a two-register catalogue names each
     * group after its register, which a reader needs anyway.
     */
    deviationSortField: {
      devNumber: 'number',
      title: 'title',
      kind: 'kind',
      status: 'status',
      createdAt: 'created',
    },
    column: {
      number: 'Number',
      title: 'Title',
      kind: 'Kind',
      status: 'Status',
      departedFrom: 'Departed from',
      window: 'Window',
    },
    /** The row link — `DEV-2026-0007 · Skipped rinse step`. The number is an
     * identifier, not a word; only the separator is the locale's. */
    empty: 'No deviations match these filters.',
  },

  create: {
    button: 'New deviation',
    title: 'New deviation',
    fieldTitle: 'Title',
    description: 'Description',
    /** The description placeholder asks a different question for each kind —
     * a planned departure has not happened yet. */
    descriptionPlanned: 'What will be done differently',
    descriptionUnplanned: 'What happened',
    departedFrom: 'Procedure / spec departed from',
    justification: 'Justification',
    riskAssessment: 'Risk assessment (impact + likelihood)',
    lot: 'Affected lot (optional)',
    windowOpens: 'Window opens',
    windowCloses: 'Window closes',
    /** FF-AUDIT-078: field-level message for a window that closes before it opens. */
    windowReversed: 'The window must close after it opens.',
    /** FF-QA-084 (owner decision 2026-09-27): raising a PLANNED deviation is
     * management's call (or a named submitter's) — the Planned card is
     * disabled with this sentence for everyone else. */
    plannedRestricted: 'Only management or a named planned-deviation submitter can raise a planned deviation. Record what happened as an unplanned deviation.',
    /** Shown while Planned is selected: the draft is not sent anywhere on save. */
    plannedDraftNote: 'A planned deviation is saved as a draft. Open it and choose “Submit for approval” when it is ready — management is told then, not now.',
    /** The success note after saving, by kind. */
    savedPlanned: '{number} saved as a draft. Submit it for approval when it is ready.',
    savedUnplanned: '{number} recorded. Send it for review when the record is complete.',
    cancel: 'Cancel',
    submit: 'Create',
  },

  detail: {
    notFound: 'Deviation not found.',
    loading: 'Loading…',
    /** The arrow is part of the string, as in `ncr` and `workforce`. */
    back: '← Back to deviations',
    /** Fiix audit Phase 2: the header overflow's accessible name. */
    moreActions: 'More actions for this deviation',
    /** The record rail (Fiix audit Phase 2): the facts the body used to carry
     * in the departure record and the workflow aside. */
    facts: {
      region: 'Deviation facts',
      kind: 'Kind',
      departedFrom: 'Departed from',
      raised: 'Raised',
      nonconformance: 'Nonconformance',
      correctiveAction: 'Corrective action',
      archived: 'Archived',
    },
    /** The header sentence when the record names the procedure it departs
     * from. `{summary}` is one of `kindSummary`; the whole thing is one
     * message so the clause order is the locale's. */
    summaryWithSource: '{summary} · from {source}',
    editDetails: 'Edit details',
    evidenceBundle: 'Evidence bundle',
    restore: 'Restore',
    archive: 'Archive',
    departureRecord: 'Departure record',
    description: 'Description',
    justification: 'Justification',
    riskAssessment: 'Risk assessment',
    notRecorded: 'Not recorded',
    effectiveWindow: 'Effective window',
    approved: 'Approved',
    rejected: 'Rejected',
    /** The approval stamp when a comment was left with the decision. Without
     * one the date renders alone. */
    approvalStamp: '{date} — {comment}',
    evidenceFiles: 'Evidence files',
    attach: 'Attach',
    sendForReview: 'Send for review',
    /** FF-QA-084 (owner decision 2026-09-27): the planned draft's one next step. */
    submitForApproval: 'Submit for approval',
    /** The status line once submitted — the owner's words, with the date it was sent. */
    sentForApproval: 'Sent to management for approval — {date}',
    /** Beneath that line for a PLANNED deviation, which cannot be edited until management decides. */
    lockedUnderReview: 'Locked while management decides. If it is rejected, it reopens as a draft.',
    /** A planned DRAFT nobody has been told about yet. */
    draftNotSent: 'This draft has not been sent for approval. Submit it when it is ready — management is told then.',
    /** Has the approve permission but is not in the company's approver set. */
    approverNote: 'Only management or a named approver can approve this deviation.',
    approval: 'Approval',
    approveBeforeWindow: 'Must be approved before the window opens.',
    /** Owner decision 2026-09-16 (round 2): requester ≠ approver. */
    selfApprovalNote: 'You raised this deviation, so someone else must approve it.',
    comment: 'Comment (required to reject)',
    /** Part 11 signs deviation approvals when e-sign is in scope. */
    signAndApprove: 'Sign & approve',
    approve: 'Approve',
    reject: 'Reject',
    close: 'Close deviation',
    closed: 'Closed',
    /** FF-AUDIT-063: closing asks first and records a closure statement. */
    closeDialog: {
      title: 'Close {number}?',
      description: 'A closed deviation is final. The statement below stays on the record and in the audit log.',
      note: 'Closure statement (optional)',
      notePlaceholder: 'e.g. Window ended on schedule; no product impact observed.',
      keep: 'Not yet',
      confirm: 'Close deviation',
      closing: 'Closing…',
    },
    viewNcr: 'View NCR',
    raiseNcr: 'Raise NCR for material',
    viewCar: 'View CAR',
    raiseCar: 'Raise CAR',
  },

  edit: {
    /** `RecordEditDialog`'s heading. `DEV-2026-0007` is an identifier. */
    title: 'Edit {number}',
    fieldTitle: 'Title',
    description: 'Description',
    departedFrom: 'Departed from',
    departedFromHelp: 'The procedure, spec or limit this departs from.',
    justification: 'Justification',
    riskAssessment: 'Risk assessment',
    effectiveFrom: 'Effective from',
    effectiveTo: 'Effective to',
  },

  oos: {
    /**
     * QMS-004 / NAV-002 (owner, 2026-08-16): the acronym is expanded on every
     * FIRST-USE surface — this heading, the nav entry, the detail title, the
     * back link and the two open-an-investigation actions. It stays SHORT
     * (`OOS`) in filters, columns, empty states, load failures and record
     * numbers, which is the audit's own principle 3: introduce it once, then
     * use it. The French has said the whole phrase all along — see
     * `copy.fr.ts`'s header — so this closes an EN↔FR gap rather than opening
     * one.
     */
    title: 'Out-of-spec investigations (OOS)',
    description:
      'Out-of-spec results run a phased investigation: rule out lab error first, then the full manufacturing review.',
    exportLabel: 'Export',
    statusFilter: 'Filter by status',
    allStatuses: 'All statuses',
    conclusionFilter: 'Filter by conclusion',
    anyConclusion: 'Any conclusion',
    from: 'From',
    to: 'To',
    search: 'Search investigations',
    toDeviations: 'Deviations',
    loading: 'Loading…',
    loadFailed: 'OOS investigations could not be loaded.',
    region: 'Out-of-specification results',
    /** One entry per field in `OosSort`; `createdAt` is the default sort and
     * has a column here, but the guard reads the spec either way. Named for
     * its register — see `deviations.deviationSortField`. */
    oosSortField: {
      oosNumber: 'number',
      status: 'status',
      createdAt: 'opened',
    },
    column: {
      number: 'Number',
      source: 'Source',
      result: 'Result vs spec',
      status: 'Status',
      phase1: 'Phase 1',
      conclusion: 'Conclusion',
      opened: 'Opened',
    },
    empty: 'No OOS investigations match these filters.',
  },

  /** The measured value beside its unit, and the spec band under it. Both are
   * whole messages: a unit's position relative to its number is a locale
   * decision, and so are the band's brackets. */
  resultValue: '{value} {unit}',
  specRange: '({low} .. {high})',

  oosDetail: {
    notFound: 'OOS investigation not found.',
    loading: 'Loading…',
    back: '← Back to out-of-spec investigations',
    /** The record title under the OOS number — the acronym is already on
     * screen, in the record number, where it is an identifier and not a word. */
    recordTitle: 'Out-of-spec investigation',
    moreActions: 'More actions for this investigation',
    /** The record rail (Fiix audit Phase 2). */
    facts: {
      region: 'Investigation facts',
      source: 'Source',
      sourceCheck: 'Source check',
      raised: 'Opened',
      concluded: 'Concluded',
      nonconformance: 'Nonconformance',
      correctiveAction: 'Corrective action',
      archived: 'Archived',
    },
    evidenceBundle: 'Evidence bundle',
    restore: 'Restore',
    archive: 'Archive',
    resultVsSpec: 'Result vs specification',
    /** FF-AUDIT-070: under the limit the result breaks. */
    violated: 'Limit exceeded',
    result: 'Result',
    specLow: 'Spec low',
    specHigh: 'Spec high',
    phase1Heading: 'Phase 1 — lab / measurement assessment',
    phase1Placeholder: 'Retest, calibration check, sampling review — what was found?',
    recordPhase1: 'Record Phase 1',
    notRecordedYet: 'Not recorded yet.',
    phase2Heading: 'Phase 2 — full manufacturing investigation',
    phase2Placeholder: 'Manufacturing review — batch records, equipment, materials, people',
    rootCause: 'Root cause',
    recordPhase2: 'Record Phase 2',
    /** Why Phase 2 is absent — two different reasons, two whole sentences. */
    phase2NotNeeded: 'Not needed — Phase 1 found a measurement error.',
    phase2Waiting: 'Waits for an escalated Phase 1.',
    evidenceFiles: 'Evidence files',
    attach: 'Attach',
    conclusionHeading: 'Conclusion',
    /** Four buttons rather than a prefix plus a verdict: "Sign &" is not a
     * separable fragment in French, and the two verdicts read differently. */
    concludeConfirmed: 'Conclude: confirmed out-of-spec result',
    signAndConcludeConfirmed: 'Sign & conclude: confirmed out-of-spec result',
    concludeInvalidated: 'Conclude: invalidated',
    /** Owner decision 2026-09-16 (round 2): the opener cannot conclude. */
    selfConclusionNote: 'You opened this investigation, so someone else must conclude it.',
    signAndConcludeInvalidated: 'Sign & conclude: invalidated',
    /** FF-AUDIT-087: invalidating after Phase 2 states its assignable cause first. */
    invalidateWithCause: 'Invalidate the result (assignable cause)…',
    invalidateAfterPhase2: 'Phase 1 escalated this result and Phase 2 was recorded. Invalidating it now contradicts the investigation unless an assignable cause explains the result — state it; it is signed onto the record.',
    invalidationReason: 'Assignable cause',
    invalidationReasonPlaceholder: 'e.g. sample mislabelled at receipt; re-test of the retained sample conforms',
    keepInvestigating: 'Back',
    invalidationReasonLabel: 'Invalidated because:',
    close: 'Close investigation',
    concluded: 'Concluded {date}',
    viewNcr: 'View NCR',
    raiseNcr: 'Raise material NCR',
    viewCar: 'View CAR',
    raiseCar: 'Raise root-cause CAR',
    viewSourceCheck: 'View source check',
  },

  /** `OpenOosButton` — published to any module that can produce an
   * out-of-limits reading (a check, an incoming inspection). */
  openOos: {
    button: 'Open an out-of-spec investigation',
    title: 'Open an out-of-spec investigation',
    description:
      'Record the out-of-limits result; Phase 1 rules out a lab or measurement error first.',
    result: 'Result',
    unit: 'Unit',
    specLow: 'Spec low',
    specHigh: 'Spec high',
    /** FF-AUDIT-070: the two refusals, shown before Submit and enforced by the server. */
    resultInSpec: 'The result is inside its specification. An out-of-spec investigation needs a result outside the limits.',
    /** FF-QA-166 */
    resultMissing: 'Record the measured result.',
    limitsMissing: 'Record at least one specification limit.',
    specRangeInverted: 'The low limit is above the high limit.',
    lot: 'Affected lot (optional)',
    cancel: 'Cancel',
    submit: 'Open investigation',
    submitting: 'Opening…',
  },

  /** `RaiseDeviationButton` — the escalation from a failed check. */
  raise: {
    button: 'Record deviation',
    title: 'Record unplanned deviation',
    fieldTitle: 'Title',
    description: 'What happened',
    departedFrom: 'Procedure / spec departed from',
    cancel: 'Cancel',
    submit: 'Record deviation',
    submitting: 'Recording…',
  },

  /** FF-AUDIT-074: Raise NCR for material from a deviation or a confirmed OOS. */
  raiseFrom: {
    ncTitle: 'Raise NCR for material',
    ncDescription: 'State what is affected. The quantity drives the material hold, the disposition ceiling and the cost of quality.',
    quantity: 'Affected quantity',
    unit: 'Unit',
    severity: 'Severity',
    lot: 'Lot (optional)',
    cancel: 'Cancel',
    submit: 'Raise NCR',
    submitting: 'Raising…',
    failed: 'Could not raise the NCR.',
  },
})

export type DeviationsOosCopy = typeof deviationsOosCopy.en
